import React, {
  useCallback, useContext, useEffect, useState, useRef,
} from 'react';
import LinkInvitationView from 'screens/LinkInvitation/components/View';
import InvitationsServices from 'services/api/Invitations';
import handlePromise from 'utils/Promise';
import { LoadingContext } from 'context/Loading';
import { useParams } from 'react-router';
import Session from 'services/session/Session';
import { LinkExternalSteps } from 'constants/utils';
import { ModalErrorContext } from 'context/ModalError';
import { useTranslation } from 'react-i18next';
import { generarHash } from 'utils/utils';
import { InvitationData } from 'types/Invitation';

function LinkInvitation() {
  const { t } = useTranslation();
  const { uuid } = useParams();
  const { toggleLoading } = useContext( LoadingContext );
  const { openErrorModal } = useContext( ModalErrorContext );
  const [step, setStep] = useState<LinkExternalSteps>( LinkExternalSteps.default );
  const [invitationData, setInvitationData] = useState<InvitationData | null>( null );
  const hasLoadedRef = useRef( false );
  const documentoNdaRef = useRef( '' );

  const [documents, setDocuments] = useState<any[]>( [] );

  const handleCloseInvitationAccess = useCallback( () => {
    Session.clearToken();
    setInvitationData( null );
    setStep( LinkExternalSteps.finish );
  }, [] );

  const handleGetInvitationDocuments = useCallback( async () => {
    const [asyncError, asyncResponse] = await handlePromise(
      InvitationsServices.getInvitationDocuemntsAsync( {
        InvitacionUid: uuid || '',
      } ),
    );

    if ( !asyncResponse.ok ) {
      if ( asyncError ) {
        openErrorModal( asyncError.toString() );
      } else {
        openErrorModal( 'Ha ocurrido un error al procesar los documentos. Intente de nuevo más tarde.' );
      }
      return;
    }

    // Si getInvitationDocuemntsAsync fue exitoso, continuar con getInvitationDocuemnts
    const [, response, data] = await handlePromise(
      InvitationsServices.getInvitationDocuemnts( {
        InvitacionUid: uuid || '',
      } ),
    );

    if ( !response.ok ) {
      openErrorModal( 'No se pudieron obtener los documentos. Intente de nuevo más tarde.' );
    } else if ( Array.isArray( data ) ) {
      setDocuments( data );

      setStep( LinkExternalSteps.summary );
    }
  }, [uuid] );

  const getInvitationData = useCallback( async ( nextStep?: LinkExternalSteps ) => {
    toggleLoading( true );
    try {
      const [error, response, data] = await handlePromise(
        InvitationsServices.getInvitation( {
          InvitacionUid: uuid || '',
        } ),
      );

      if ( !response.ok ) {
        if ( response.status === 401 ) {
          setTimeout( () => {
            setStep( LinkExternalSteps.code );
          }, 1500 );
        } else
        if ( response.status === 400 ) {
          openErrorModal( t( `admin.invitations.errors.${error.toString()}` ) );
          handleCloseInvitationAccess();
        } else {
          openErrorModal( 'Ha ocurrido un error inesperado. Pruebe en unos minutos.' );
        }
        return;
      }

      if ( data ) {
        setInvitationData( data );

        if (
          data.ndaEstado?.toUpperCase() === 'COMPLETADO'
          && data.estadoInvitacion?.toUpperCase() === 'CREADA'
        ) {
          await handleGetInvitationDocuments();
          return;
        }

        if ( nextStep ) setStep( nextStep );
      }
    } finally {
      setTimeout( () => {
        toggleLoading( false );
      }, 800 );
    }
  }, [uuid] );

  const handleSubmitCode = useCallback( async ( code: string ) => {
    toggleLoading( true );
    documentoNdaRef.current = '';

    const [error, response, invitacionCodeData] = await handlePromise(
      InvitationsServices.getInvitationCode( {
        InvitacionUid: uuid || '',
        CodigoControl: code,
      } ),
    );
    if ( !response.ok ) {
      if ( error ) {
        openErrorModal( t( `admin.invitations.errors.${error.toString()}` ) );
      } else {
        openErrorModal( 'Ha ocurrido un error inexperado. Pruebe en unos minutos' );
      }
    } else if ( invitacionCodeData?.token ) {
      Session.setToken( invitacionCodeData.token );
      await getInvitationData( LinkExternalSteps.information );
    }
    toggleLoading( false );
  }, [uuid] );

  const handleSubmitNda = useCallback( async ( ) => {
    toggleLoading( true );

    const [error, response, invitationNdaData] = await handlePromise(
      InvitationsServices.postInvitationNda(
        {
          nombre: undefined,
          apellidos: undefined,
          domicilio: undefined,
          nfi: undefined,
          nacionalidad: undefined,
          tipoPersona: undefined,
        },
        {
          InvitacionUid: uuid || '',
        },
      ),
    );

    if ( !response.ok ) {
      if ( error ) {
        openErrorModal( error.toString() );
      } else {
        openErrorModal( 'Ha ocurrido un error inexperado. Pruebe en unos minutos' );
      }
      setTimeout( () => {
        toggleLoading( false );
      }, 800 );
    } else if ( invitationNdaData?.invitacion?.invitacionUid && invitationNdaData?.documentoNda ) {
      documentoNdaRef.current = invitationNdaData?.documentoNda;
      await getInvitationData( LinkExternalSteps.warning );
    }
  }, [uuid] );

  const handleSignNda = useCallback( async () => {
    toggleLoading( true );
    if ( !invitationData?.ndaUid ) {
      return false;
    }
    const codigoHash = await generarHash( '-' );
    const [error, response, data] = await handlePromise(
      InvitationsServices.postInvitationSimulacionNda(
        {
          ndaUid: invitationData?.ndaUid,
          codigo: codigoHash,
        },
        { InvitacionUid: uuid || '' },
      ),
    );
    if ( !response.ok ) {
      if ( error ) {
        openErrorModal( error.toString() );
      } else {
        openErrorModal( 'Ha ocurrido un error inexperado. Pruebe en unos minutos' );
      }
    } else if ( data?.invitacion?.ndaEstado === 'COMPLETADO' ) {
      await handleGetInvitationDocuments();
      toggleLoading( false );
    } else {
      openErrorModal( 'Ha ocurrido un error inexperado. Pruebe en unos minutos' );
      toggleLoading( false );
    }
  }, [invitationData?.ndaUid, uuid] );

  const goToNextStep = useCallback( ( values: any ) => {
    switch ( step ) {
      case LinkExternalSteps.code:
        handleSubmitCode( values?.code || '' );
        break;
      case LinkExternalSteps.information:
        handleSubmitNda( );
        break;
      case LinkExternalSteps.warning:
        setStep( LinkExternalSteps.signature );
        break;
      case LinkExternalSteps.signature:
        handleSignNda();
        break;
      default:
        setStep( LinkExternalSteps.type );
    }
  }, [step] );

  const goToPreviousStep = () => {
    switch ( step ) {
      case LinkExternalSteps.code:
        getInvitationData( LinkExternalSteps.default );
        break;
      case LinkExternalSteps.warning:
        setStep( LinkExternalSteps.code );
        break;
      case LinkExternalSteps.signature:
        setStep( LinkExternalSteps.warning );
        break;
      case LinkExternalSteps.summary:
        setStep( LinkExternalSteps.signature );
        break;
      default:
        setStep( LinkExternalSteps.type );
    }
  };

  useEffect( () => {
    toggleLoading( false );
    if ( !hasLoadedRef.current ) {
      hasLoadedRef.current = true;
      getInvitationData( LinkExternalSteps.information );
    }
  }, [] );

  return (
    <LinkInvitationView
      step={step}
      onNext={goToNextStep}
      onPrevious={goToPreviousStep}
      handleSubmitNda={handleSubmitNda}
      invitationData={invitationData}
      documents={documents}
      onCloseInvitationAccess={handleCloseInvitationAccess}
      pdfNdaUrl={documentoNdaRef.current}
    />
  );
}

export default LinkInvitation;
